import { useState, useEffect, useMemo } from "react";
import { useParams, useLocation } from "react-router";
import { User } from "../common/types";
import useListUsers from "../../api/useListUsers";
import UserInfo from "../common/UserInfo";

const Edit = () => {
  const location = useLocation();
  const newlyCreatedUser = location.state?.newlyCreatedUser;
  const { id } = useParams<{ id: string }>();
  const userId = Number(id);

  const {
    data: allUsers = [],
    team: allTeams = [],
    isLoading: isUsersLoading,
  } = useListUsers({});

  const archivedUserSnapshot = location.state?.userSnapshot;

  const sourceUser = useMemo(() => {
    const existingUser = allUsers.find((u) => Number(u.id) === userId);
    return newlyCreatedUser || existingUser || archivedUserSnapshot;
  }, [newlyCreatedUser, archivedUserSnapshot, allUsers, userId]);
  const isArchived = sourceUser?.attributes?.active === false;

  const [userAttributes, setUserAttributes] = useState<User | null>(null);

  useEffect(() => {
    if (!sourceUser || allTeams.length === 0 || userAttributes !== null) return;

    const parsedRoles =
      sourceUser.attributes.roles?.map((role) => ({
        id: role.id,
        name: role.name ?? "",
      })) ?? [];

    const rawTeams = sourceUser.relationships?.teams?.data;

    const parsedTeams = Array.isArray(rawTeams)
      ? rawTeams.map((team) => ({
          id: Number(team.id),
          type: team.type ?? "teams",
        }))
      : [];

    const originalAttributes = {
      active: sourceUser.attributes.active,
      name: sourceUser.attributes.name,
      phone: sourceUser.attributes.phone ?? "",
      email: sourceUser.attributes.email,
      title: sourceUser.attributes.title ?? "",
      roles: parsedRoles,
      userType: sourceUser.attributes?.["user-type"] ?? "",
    };

    const originalRelationships = {
      teams: {
        data: parsedTeams,
      },
    };

    setUserAttributes({
      id: Number(sourceUser.id),
      type: sourceUser.type,
      attributes: { ...originalAttributes },
      relationships: { teams: { data: parsedTeams } },
      originalAttributes,
      originalRelationships,
    });
  }, [sourceUser, allTeams, userAttributes]);

  const roleOptions = allUsers
    .flatMap((user) => user.attributes.roles || [])
    .reduce((acc, role) => {
      const exists = acc.find((r) => r.value === String(role.id));
      if (role.name && !exists) {
        acc.push({ label: role.name, value: String(role.id) });
      }
      return acc;
    }, [] as { label: string; value: string }[]);

  const teamOptions = allTeams.map((t) => ({
    label: t.attributes.name,
    value: String(t.id),
  }));

  const onSubmit = () => {};

  if (
    isUsersLoading ||
    !sourceUser ||
    allTeams.length === 0 ||
    userAttributes === null
  ) {
    return <div>Loading...</div>;
  }

  return (
    <UserInfo
      mode="edit"
      user={userAttributes}
      setUserAttributes={setUserAttributes}
      onSubmit={onSubmit}
      roles={roleOptions}
      team={teamOptions}
      isArchived={isArchived}
    />
  );
};

export default Edit;
